import LoggedUser from "../components/LoggedUser";
import { useNavigate } from "react-router-dom";
import Logo from "./Logo";
import {
  Badge,
  Button,
  Col,
  Drawer,
  List,
  Row,
  Space,
  theme,
  Typography,
} from "antd";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Icons } from "../assets/icons";
import Icon from "@ant-design/icons";
import { useGuard } from "../Guard";
import LiveNotificationToasts from "./LiveNotificationToasts";
import dayjs from "dayjs";
import { SearchQuery } from "../code/search-query/search-query";
import { retry } from "../utils/http";
import HorizontalMenu from "../controls/HorizontalMenu";
import {
  acknowledgeDelivery,
  getMyNotifications,
  readMyNotification,
} from "../repository/data.repository";

export interface NavigationPanelProps {
  onMenuSelect: (key: string) => void;
}

interface InboxNotification {
  readonly uid: string;
  readonly title: string;
  readonly message: string;
  readonly createdAt: string;
  readonly isRead: boolean;
}

function NavigationPanel(props: NavigationPanelProps) {
  const { token } = theme.useToken();
  const { on, off } = useGuard();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<InboxNotification[]>([]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications],
  );

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    const onReceiveNotification = (
      recipientUid: string,
      _notificationUid: string,
      title: string,
      message: string,
      createdAt: string,
    ) => {
      acknowledgeDelivery({ uid: recipientUid }).catch(() => {});

      const item: InboxNotification = {
        uid: recipientUid,
        title,
        message,
        createdAt,
        isRead: false,
      };

      setNotifications((current) => [
        item,
        ...current.filter((n) => n.uid !== item.uid),
      ]);
    };

    on("ReceiveNotification", onReceiveNotification);
    return () => off("ReceiveNotification", onReceiveNotification);
  }, [on, off]);

  const openDrawer = useCallback(() => setOpen(true), []);

  function onGoHome(): void {
    navigate("./", { replace: true });
  }

  function onNotificationRead(uid: string): void {
    setNotifications((current) =>
      current.map((n) => (n.uid === uid ? { ...n, isRead: true } : n)),
    );

    readMyNotification({ uid }).catch(() =>
      setNotifications((current) =>
        current.map((n) => (n.uid === uid ? { ...n, isRead: false } : n)),
      ),
    );
  }

  function loadNotifications(): void {
    const searchQuery: SearchQuery = { conditions: [] };

    setLoading(true);

    retry(() =>
      getMyNotifications({ searchQuery, pageNumber: 0, pageSize: 100 }),
    )
      .then((result) => {
        setLoading(false);
        setNotifications(
          result.data
            .map(({ uid, title, message, createdOn, isRead }) => ({
              uid,
              title,
              message,
              createdAt: createdOn,
              isRead,
            }))
            .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
        );
        result.data
          .filter((item) => !item.deliveredAt)
          .forEach((item) =>
            acknowledgeDelivery({ uid: item.uid }).catch(() => {}),
          );
      })
      .catch(() => setLoading(false));
  }

  return (
    <Row className="flex flex-row items-center justify-between p-2">
      <Row
        className="flex cursor-pointer flex-row items-center"
        onClick={onGoHome}
      >
        <Logo className="ml-2 mr-2 h-5" />
        <Typography.Text className="pl-2 pr-2 text-base font-bold uppercase opacity-60">
          Company Admin
        </Typography.Text>
      </Row>
      <HorizontalMenu onMenuSelect={props.onMenuSelect} />
      <Space className="flex flex-row flex-nowrap items-center">
        <Badge count={unreadCount} size="small">
          <Button
            type={"text"}
            aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
            icon={
              <Icon className="text-3xl" component={Icons.NotificationsIcon} />
            }
            onClick={() => setOpen(true)}
          />
        </Badge>
        <LoggedUser />
      </Space>
      <Drawer
        open={open}
        title="Notifications"
        placement="right"
        onClose={() => setOpen(false)}
      >
        <List
          loading={loading}
          dataSource={notifications}
          locale={{ emptyText: "You're all caught up" }}
          rowKey={(item: InboxNotification) => item.uid}
          renderItem={(item) => (
            <List.Item
              className={item.isRead ? "opacity-60" : ""}
              actions={
                item.isRead
                  ? []
                  : [
                      <Button
                        key="read"
                        size="small"
                        type="text"
                        aria-label="Mark as read"
                        icon={
                          <Icon
                            className="text-2xl"
                            style={{ color: token.colorTextDescription }}
                            component={Icons.MarkEmailReadIcon}
                          />
                        }
                        onClick={() => onNotificationRead(item.uid)}
                      />,
                    ]
              }
            >
              <List.Item.Meta
                title={item.title}
                description={
                  <Col className="flex flex-col">
                    <Typography.Text
                      style={{ color: token.colorTextDescription }}
                    >
                      {item.message}
                    </Typography.Text>
                    <Typography.Text
                      className="text-xs"
                      style={{ color: token.colorTextDescription }}
                    >
                      {dayjs(item.createdAt).format(
                        "dddd, MMMM D, YYYY, hh:mm",
                      )}
                    </Typography.Text>
                  </Col>
                }
              />
            </List.Item>
          )}
        />
      </Drawer>
      <LiveNotificationToasts onOpen={openDrawer} />
    </Row>
  );
}

export default memo(NavigationPanel);
